import { useCallback, useEffect, useRef, useState } from 'react';
import {
    DayPickerDefaultProps,
    DayPickerMultipleProps,
    DayPickerProps,
    DayPickerRangeProps,
} from 'react-day-picker';
import { useWindowWidth } from '@/service/hooks/useWindowWidth';
import { DayPicker } from '../../Pickers/DayPicker';
import { Overlay } from '../../Overlay/Overlay';
import { IconButton } from '../../Buttons/IconButton';
import styles from 'react-day-picker/dist/style.module.css';
import { format } from 'date-fns';
import { cn } from '@/service/utils/cn';
import useOnClickOutside from '@/service/hooks/useClickOutside';
import { DateIcon } from '../../Icons/Date';

type TCalendarDropdown = (
    | DayPickerDefaultProps
    | DayPickerProps
    | DayPickerMultipleProps
    | DayPickerRangeProps
) & {
    isShown?: boolean;
    hideWeek?: boolean;
    isWidget?: boolean;
    showCalendar?: () => void;
    hideCalendar?: () => void;
    goToPrevWeek?: () => void;
    goToNextWeek?: () => void;
    prevWeekDisabled?: boolean;
    currentMonthDisabled?: boolean;
    nextWeekDisabled?: boolean;
    dropdownClassName?: string;
    selected: Date;
};

export const CalendarDropdown = ({
    isShown,
    showCalendar,
    hideCalendar,
    goToPrevWeek,
    goToNextWeek,
    prevWeekDisabled,
    currentMonthDisabled,
    nextWeekDisabled,
    hideWeek,
    dropdownClassName,
    isWidget,
    ...dayPickerProps
}: TCalendarDropdown) => {
    const [calendarIsShown, setCalendarIsShown] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (isShown) setCalendarIsShown(isShown);
        else setCalendarIsShown(false);
    }, [isShown]);

    const { mobile } = useWindowWidth();

    const onShow = useCallback(() => {
        if (showCalendar) showCalendar();
        else setCalendarIsShown(true);
    }, [showCalendar]);

    const onHide = useCallback(() => {
        if (hideCalendar) hideCalendar();
        else setCalendarIsShown(false);
    }, [hideCalendar]);

    useOnClickOutside(ref, onHide);

    return (
        <>
            <Overlay isShown={calendarIsShown} onHide={onHide} />
            <div
                data-test-id="calendar-dropdown"
                className={cn(
                    hideWeek
                        ? 'fixed left-0 z-50 flex w-full justify-center md:absolute md:left-auto md:w-auto'
                        : 'relative mx-auto flex h-[35px] w-full max-w-[272px] items-center justify-center rounded-[50px] border border-blue-10 bg-blue-9 dark:border-blue-1 dark:bg-blue-dark md:h-[45px] md:max-w-[415px]',
                    dropdownClassName,
                    isWidget && 'border-none bg-transparent dark:bg-transparent'
                )}
            >
                {!hideWeek && (
                    <div className="flex w-full items-center justify-around">
                        <IconButton
                            className={cn(
                                'tx-large-semibold hidden items-center gap-x-1 text-gray-2 disabled:tx-large-medium disabled:cursor-default disabled:text-gray-3 dark:text-gray-3 disabled:dark:text-dark-1 md:flex',
                                isWidget && 'flex'
                            )}
                            icon={
                                <div className="h-3 w-3 bg-arrow-left bg-contain bg-no-repeat dark:bg-arrow-left-dark" />
                            }
                            label={mobile ? 'Prev' : 'Prev week'}
                            onClick={goToPrevWeek}
                            disabled={prevWeekDisabled}
                            data-test-id="prev-week"
                        />
                        <button
                            type="button"
                            className={cn(
                                'cursor-pointer text-base font-semibold leading-[22px] text-gray-1 hover:underline disabled:cursor-default disabled:hover:no-underline dark:text-white',
                                currentMonthDisabled ? ' text-gray-4 dark:text-gray-4' : '',
                                isWidget &&
                                    'mx-3 flex items-center whitespace-nowrap rounded-3xl border border-brand px-3 py-1 !text-brand'
                            )}
                            onClick={onShow}
                            disabled={currentMonthDisabled}
                            data-test-id="calendar-date-btn"
                        >
                            {!!isWidget && (
                                <DateIcon className="mr-1 md:h-7 md:w-7 [&>*]:fill-brand" />
                            )}
                            {format(dayPickerProps.selected, 'MMMM yyyy')}
                        </button>
                        <IconButton
                            className={cn(
                                'tx-large-semibold hidden flex-row-reverse items-center gap-x-2 text-gray-2 disabled:tx-large-medium disabled:cursor-default disabled:text-gray-3 dark:text-gray-3 disabled:dark:text-dark-1 md:flex',
                                isWidget && 'flex'
                            )}
                            icon={
                                <div className="h-3 w-3 bg-arrow-right bg-contain bg-no-repeat dark:bg-arrow-right-dark" />
                            }
                            label={mobile ? 'Next' : 'Next week'}
                            onClick={goToNextWeek}
                            disabled={nextWeekDisabled}
                            data-test-id="next-week"
                        />
                    </div>
                )}
                {calendarIsShown && (
                    <div
                        className={cn(
                            'top-14 z-50 flex w-[324px] flex-col items-center rounded-[15px] bg-white shadow-lightPopup dark:bg-dark-3 dark:shadow-darkCard md:w-[324px] md:px-[22px] md:pb-3.5 md:pt-0',
                            hideWeek
                                ? 'relative md:-left-1/4'
                                : 'absolute left-1/2  -translate-x-1/2'
                        )}
                        ref={ref}
                    >
                        <DayPicker {...dayPickerProps} classNames={{ ...styles }} />
                    </div>
                )}
            </div>
        </>
    );
};
